<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    row-key="key"
    :row-selection="rowSelection"
    @change="onChange"
  >
    <template #bodyCell="{ column, record }">
      <!-- Name + avatar -->
      <template v-if="column.key === 'Name'">
        <div class="d-flex align-items-center">
          <a href="javascript:void(0);">
            <DqAvatar
              :src="record.Image"
              :name="record.name"
              size-class="avatar-md"
              shape-class="rounded-circle"
            />
          </a>
          <div class="ms-2">
            <p class="text-dark mb-0">{{ record.name }}</p>
            <span class="fs-12">{{ record.role }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'EmployeeID'">
        <span class="badge bg-light text-dark">{{ record.emp_code || '-' }}</span>
      </template>

      <template v-else-if="column.key === 'Year'">
        {{ year }}
      </template>

      <!-- Actions -->
      <template v-else-if="column.key === 'Actions'">
        <div class="d-flex gap-2">
          <button
            class="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
            title="Download P9 (PDF)"
            :disabled="loading || isBusy(record.id)"
            @click="!isBusy(record.id) && $emit('download', record)"
            v-can="'Reports.Export'"
          >
            <span
              v-if="isDownloading(record.id)"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <i v-else class="ti ti-download"></i>
            <span class="ms-1 d-none d-md-inline">Download</span>
          </button>

          <button
            class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
            title="Email P9 to employee"
            :disabled="loading || isBusy(record.id) || record.missing_email"
            @click="!isBusy(record.id) && !record.missing_email && $emit('email', record)"
          >
            <span
              v-if="isEmailing(record.id)"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <i v-else class="ti ti-mail"></i>
            <span class="ms-1 d-none d-md-inline">Email</span>
          </button>
        </div>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";
import { isMissingEmployeeEmail } from "@/utils/employeeEmail";

/**
 * Props
 *  - rows: Employee[] minimal data for listing
 *  - loading: boolean
 *  - total, pageSize, current: for controlled pagination (parent decides)
 *  - year: selected year (display only)
 *  - downloadingIds: Array|Set of employee ids currently downloading
 *  - emailingIds: Array|Set of employee ids currently emailing
 */
const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
  year: { type: Number, required: true },
  downloadingIds: { type: [Array, Set, Object], default: () => [] },
  emailingIds: { type: [Array, Set, Object], default: () => [] },
  selectedRowKeys: { type: Array, default: () => [] },
});
const emit = defineEmits(["change", "download", "email", "selection-change"]);

const rowSelection = computed(() => ({
  selectedRowKeys: props.selectedRowKeys,
  preserveSelectedRowKeys: true,
  onChange: (keys) => {
    emit("selection-change", (keys || []).map((k) => Number(k)));
  },
}));

const columns = [
  { sorter: false }, // avatar col
  {
    title: "Name",
    dataIndex: "name",
    key: "Name",
    sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
  },
  {
    title: "Emp ID",
    dataIndex: "emp_code",
    key: "EmployeeID",
    sorter: (a, b) => (a.emp_code || "").localeCompare(b.emp_code || ""),
    width: 120,
  },
  {
    title: "Year",
    dataIndex: "year",
    key: "Year",
    sorter: false,
    width: 90,
  },
  {
    title: "Actions",
    key: "Actions",
    sorter: false,
    width: 170,
  },
];

const dataSource = computed(() =>
  (props.rows || []).map((e, idx) => ({
    key: e.id || idx,
    id: e.id,
    Image: e.avatar_url || "",
    name: `${e.first_name || ""} ${e.last_name || ""}`.trim(),
    role: e.designation?.name || "-",
    emp_code: e.employee_id || "-",
    email: e.email,
    missing_email: isMissingEmployeeEmail(e.email),
  }))
);

const pagination = computed(() => ({
  total: props.total || (props.rows?.length ?? 0),
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} employees`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}

/** Helpers that accept Array, Set, or object map */
function has(setLike, id) {
  if (!setLike) return false;
  if (Array.isArray(setLike)) return setLike.includes(id);
  if (setLike instanceof Set) return setLike.has(id);
  // object map: { [id]: true }
  return !!setLike[id];
}
const isDownloading = (id) => has(props.downloadingIds, id);
const isEmailing = (id) => has(props.emailingIds, id);
/** Disable both buttons if either action is ongoing */
const isBusy = (id) => isDownloading(id) || isEmailing(id);
</script>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 760px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
